/**
 * Alpine Stores v1.0.0
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * IIFE providing Alpine stores (must be defer, not module).
 *
 * Stores:
 * - $store.cart:    Cart state + actions (THE ONE API)
 * - $store.ui:      UI state (drawers, modals)
 * - $store.pricing: Price testing + discount modules
 *
 * Page-specific stores live in:
 * - alpine-product.js    ($store.productForm, $store.variant, productForm data, productGallery data)
 * - alpine-collection.js (productGrid data, collectionSlider data)
 *
 * Access patterns:
 * - Templates: @click="$store.cart.add({ id: 123 })"
 * - Web components: Alpine.store('cart').add({ id: 123 })
 *
 * WHY IIFE (not ES Module):
 * Alpine.js fires alpine:init during defer execution. ES Modules execute
 * AFTER defer scripts. IIFE ensures stores register before Alpine initializes.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 */

(function() {
  'use strict';

  // Ensure window.Theme exists
  window.Theme = window.Theme || {};

  // Get Logger (loaded before this file)
  const Logger = window.Theme.Logger || {
    ns: () => ({
      debug: () => {},
      info: () => {},
      warn: console.warn.bind(console),
      error: console.error.bind(console)
    })
  };

  const log = Logger.ns('stores');

  // ═══════════════════════════════════════════════════════════════════════════════
  // EVENTS
  // ═══════════════════════════════════════════════════════════════════════════════
  //
  // MENTAL MODEL:
  // ─────────────────────────────────────────────────────────────────────────────
  // Events are for WEB COMPONENT communication. Alpine templates use $store.
  //
  //   Alpine Templates          Web Components
  //   ────────────────          ──────────────
  //   $store.cart.add()    →    dispatches CartUpdateEvent
  //   $store.cart.loading  ←    reactive state (no events needed)
  //
  // WHY INLINE (not imported from events.js):
  // This file is an IIFE that runs BEFORE ES Modules load (for alpine:init timing).
  // ES Modules can't be imported here. These inline classes are for DISPATCHING.
  // events.js (ES Module) provides typed classes for LISTENING in web components.
  //
  // KEEP IN SYNC: Event names here must match events.js
  // ─────────────────────────────────────────────────────────────────────────────

  const ThemeEvents = {
    cartUpdate: 'cart:update',
    cartError: 'cart:error',
    variantSelected: 'variant:selected',
    variantUpdate: 'variant:update',
  };

  class CartUpdateEvent extends Event {
    constructor(cart, sourceId = null, data = {}) {
      super(ThemeEvents.cartUpdate, { bubbles: true });
      this.detail = { cart, sourceId, data };
    }
  }

  class CartErrorEvent extends Event {
    constructor(error, sourceId = null, data = {}) {
      super(ThemeEvents.cartError, { bubbles: true });
      this.detail = { error, sourceId, data };
    }
  }

  // Expose for web components that need to dispatch events
  window.Theme.ThemeEvents = ThemeEvents;
  window.Theme.CartUpdateEvent = CartUpdateEvent;
  window.Theme.CartErrorEvent = CartErrorEvent;

  // ═══════════════════════════════════════════════════════════════════════════════
  // HELPERS
  // ═══════════════════════════════════════════════════════════════════════════════

  /**
   * Format cents to money string using shop format
   * Matches Shopify.formatMoney exactly:
   * - amount: 1,234.56 (US - comma thousands, dot decimal)
   * - amount_no_decimals: 1,234 (US)
   * - amount_with_comma_separator: 1.234,56 (EU - dot thousands, comma decimal)
   * - amount_no_decimals_with_comma_separator: 1.234 (EU)
   */
  const formatMoney = (cents, format) => {
    if (typeof cents === 'string') cents = parseInt(cents.replace(/[^0-9-]/g, ''));
    if (isNaN(cents) || cents == null) return '';

    const formatStr = format || window.Theme?.shopify?.moneyFormat || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    const parts = value.split('.');
    const dollars = parts[0];
    const centsStr = parts[1];

    // Thousands separator patterns
    const withDotThousands = dollars.replace(/\B(?=(\d{3})+(?!\d))/g, '.');   // EU: 1.234
    const withCommaThousands = dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ','); // US: 1,234

    return formatStr
      .replace(/\{\{\s*amount\s*\}\}/g, withCommaThousands + '.' + centsStr)                    // US: 1,234.56
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, withCommaThousands)                         // US: 1,234
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withDotThousands + ',' + centsStr) // EU: 1.234,56
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, withDotThousands);     // EU: 1.234
  };

  window.Theme.formatMoney = formatMoney;

  /**
   * Collect section IDs for cart updates
   * Reads from cart-drawer + any [data-cart-section] elements (e.g. cart page)
   */
  const collectCartSections = () => {
    const sections = [];
    const cartDrawer = document.querySelector('cart-drawer[data-section-id]');
    if (cartDrawer?.dataset.sectionId) sections.push(cartDrawer.dataset.sectionId);
    document.querySelectorAll('[data-cart-section]').forEach(el => {
      const sectionEl = el.closest('.shopify-section');
      if (sectionEl) {
        const id = sectionEl.id.replace('shopify-section-', '');
        if (id && !sections.includes(id)) sections.push(id);
      }
    });
    return sections;
  };

  /**
   * Morph sections using Alpine.morph() via section-renderer.js
   *
   * Uses window.Theme.morphSections (exposed by section-renderer.js ES Module).
   * The ES Module loads after this IIFE, but will be available by the time
   * user interactions trigger cart updates.
   *
   * @param {Object} sectionsHtml - { sectionId: html, ... }
   */
  const morphSections = (sectionsHtml) => {
    if (!sectionsHtml || typeof sectionsHtml !== 'object') return;

    // Use section-renderer.js (uses Alpine.morph under the hood)
    if (window.Theme?.morphSections) {
      window.Theme.morphSections(sectionsHtml);
    } else if (typeof Alpine !== 'undefined' && Alpine.morph) {
      // Fallback: use Alpine.morph directly if section-renderer not loaded
      log.warn('Using Alpine.morph fallback - section-renderer.js not yet loaded');
      Object.entries(sectionsHtml).forEach(([sectionId, html]) => {
        const el = document.querySelector(`#shopify-section-${sectionId}`);
        if (el && html) {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newContent = doc.querySelector(`#shopify-section-${sectionId}`);
          if (newContent) Alpine.morph(el, newContent);
        }
      });
    } else {
      // Last resort: innerHTML replacement
      log.warn('No morph available, using innerHTML fallback');
      Object.entries(sectionsHtml).forEach(([sectionId, html]) => {
        const el = document.querySelector(`#shopify-section-${sectionId}`);
        if (el && html) {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newContent = doc.querySelector(`#shopify-section-${sectionId}`);
          if (newContent) el.innerHTML = newContent.innerHTML;
        }
      });
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // ALPINE STORES
  // ═══════════════════════════════════════════════════════════════════════════════

  const setupAlpineStores = () => {
    if (typeof Alpine === 'undefined') return;

    const storeLog = Logger.ns('store.cart');
    const ssrCart = window.Theme?.cartData || {};

    // Register $money magic
    Alpine.magic('money', () => (cents) => formatMoney(cents));

    // ─────────────────────────────────────────────────────────────────────────────
    // $store.ui - UI state for drawers/modals
    // ─────────────────────────────────────────────────────────────────────────────
    //
    // Simple reactive values. Effect syncs store → drawer.
    // Drawer syncs back via #syncAlpineStore() when opened/closed.
    // ─────────────────────────────────────────────────────────────────────────────

    Alpine.store('ui', {
      cartOpen: false,
      menuOpen: false,
      searchMobileOpen: false
    });

    // Sync: Alpine store → Drawer web components
    // Must access properties directly for reactivity tracking
    Alpine.effect(() => {
      const ui = Alpine.store('ui');

      // Read all values first (registers reactive dependencies)
      const cartOpen = ui.cartOpen;
      const menuOpen = ui.menuOpen;
      const searchMobileOpen = ui.searchMobileOpen;

      const syncDrawer = (id, shouldBeOpen) => {
        const drawer = document.getElementById(id);
        if (!drawer || typeof drawer.open !== 'function') return;

        const isOpen = drawer.hasAttribute('data-open');

        if (shouldBeOpen && !isOpen) drawer.open();
        else if (!shouldBeOpen && isOpen) drawer.close();
      };

      syncDrawer('cart-drawer', cartOpen);
      syncDrawer('menu-drawer', menuOpen);
      syncDrawer('search-drawer', searchMobileOpen);
    });

    // ─────────────────────────────────────────────────────────────────────────────
    // $store.pricing - Price testing + discount modules
    // ─────────────────────────────────────────────────────────────────────────────
    //
    // ARCHITECTURE:
    //   Base Price → Price Testing (always) → ONE Discount Module → Rounding (if tier > 0)
    //
    // USAGE:
    //   $store.pricing.calculate(baseCents, quantity, { discountType, ...config })
    //   Returns: { perItem, total, totalSaved, discountPercent, ... }
    //
    // ─────────────────────────────────────────────────────────────────────────────

    const pricingLog = Logger.ns('store.pricing');
    const PRICING_STORAGE_KEY = 'pricing_tier';
    const PRICING_STORAGE_DAYS = 5;

    Alpine.store('pricing', {
      // ═══════════════════════════════════════════════════════════════════════════
      // STATE
      // ═══════════════════════════════════════════════════════════════════════════

      tier: null,  // Current price testing tier (null = control, any string = test tier)

      // Default config (overridden by SSR injection or metafields)
      config: {
        priceTesting: {},      // { "1": 10, "2": 20 } = tier: discount%
        quantityTiers: [],     // [{ min: 2, discount: 10 }, { min: 5, discount: 20 }]
        rounding: [] // Charm price targets (opt-in via dfs.rounding metafield)
      },

      // ═══════════════════════════════════════════════════════════════════════════
      // DISCOUNT MODULES (strategies)
      // ═══════════════════════════════════════════════════════════════════════════
      //
      // Each module takes (pricePerItem, quantity, config) and returns:
      //   { pricePerItem, quantity, discountPercent, label }
      //
      // Modules are MUTUALLY EXCLUSIVE - only one applies per calculation.
      // ═══════════════════════════════════════════════════════════════════════════

      modules: {
        // No discount module - passthrough
        none: (priceEuros, quantity, config) => ({
          pricePerItem: priceEuros,
          quantity,
          discountPercent: 0,
          label: null
        }),

        // Quantity Tiers - ALL items get % off when threshold met
        // MATCHES BACKEND: price * (1 - discount/100) - NO ROUNDING HERE
        // Rounding happens in Layer 3 (charm pricing)
        quantity_tiers: (priceEuros, quantity, config) => {
          const tiers = (config.quantityTiers || [])
            .filter(t => typeof t.minQty === 'number' && typeof t.discount === 'number');
          if (!tiers.length) {
            return { pricePerItem: priceEuros, quantity, discountPercent: 0, label: null };
          }

          // Find highest applicable tier (sorted desc by minQty)
          const applicable = tiers
            .filter(t => quantity >= t.minQty)
            .sort((a, b) => b.minQty - a.minQty)[0];

          if (!applicable) {
            return { pricePerItem: priceEuros, quantity, discountPercent: 0, label: null };
          }

          // Backend: discountedPrice = originalPrice * (1 - tier.discount / 100)
          const discounted = priceEuros * (1 - applicable.discount / 100);
          return {
            pricePerItem: discounted,
            quantity,
            discountPercent: applicable.discount,
            label: `${applicable.discount}% off (${applicable.minQty}+ items)`
          };
        }
      },

      // ═══════════════════════════════════════════════════════════════════════════
      // CORE CALCULATION
      // ═══════════════════════════════════════════════════════════════════════════

      /**
       * Calculate price with all discount layers applied
       *
       * MATCHES BACKEND EXACTLY:
       * 1. Work in EUROS (floating point) - no premature rounding
       * 2. Apply discount: price * (1 - discount/100)
       * 3. Apply charm rounding in euros
       * 4. Convert back to cents at the end
       *
       * @param {number} baseCents - Original price in cents
       * @param {number} quantity - Item quantity (default: 1)
       * @param {object} options - { discountType?, priceTesting?, quantityTiers?, rounding? }
       * @returns {object} Full pricing breakdown
       */
      calculate(baseCents, quantity = 1, options = {}) {
        const {
          discountType = null,
          priceTesting = this.config.priceTesting,
          quantityTiers = this.config.quantityTiers,
          rounding = this.config.rounding
        } = options;

        // Work in EUROS (floating point) to match backend precision
        let priceEuros = baseCents / 100;
        const baseEuros = priceEuros;

        const breakdown = {
          tierDiscount: 0,
          moduleDiscount: 0,
          discountType: discountType,
          roundingApplied: false,
          label: null
        };

        // ──────────────────────────────────────────────────────────────────────
        // LAYER 1: Price Testing (PT Cart Transform)
        // Backend: discount → round → new base price
        // Control (tier=0) = skip entirely, no discount, no rounding
        // ──────────────────────────────────────────────────────────────────────
        if (this.tier !== null && priceTesting && priceTesting[this.tier] !== undefined) {
          const tierDiscount = Number(priceTesting[this.tier]) || 0;
          if (tierDiscount > 0) {
            priceEuros = priceEuros * (1 - tierDiscount / 100);
            // PT rounds immediately after discount (separate from quantity rounding)
            if (rounding && rounding.length > 0) {
              priceEuros = this._roundToCharm(priceEuros, rounding);
            }
            breakdown.tierDiscount = tierDiscount;
          }
        }

        // ──────────────────────────────────────────────────────────────────────
        // LAYER 2: Discount Module (Quantity Discount Function)
        // Backend: reads PT-rounded price → discount → round
        // ──────────────────────────────────────────────────────────────────────
        const moduleConfig = { quantityTiers };
        const module = this.modules[discountType] || this.modules.none;
        const moduleResult = module(priceEuros, quantity, moduleConfig);

        priceEuros = moduleResult.pricePerItem;
        breakdown.moduleDiscount = moduleResult.discountPercent;
        breakdown.label = moduleResult.label;

        // Round after module discount (if module actually applied a discount)
        if (breakdown.moduleDiscount > 0 && rounding && rounding.length > 0) {
          priceEuros = this._roundToCharm(priceEuros, rounding);
          breakdown.roundingApplied = true;
        }

        // ──────────────────────────────────────────────────────────────────────
        // CONVERT BACK TO CENTS
        // Charm-rounded values land exactly on .49/.99 → Math.round is safe.
        // Without charm rounding, float errors (e.g. 25.4149999) cause
        // Math.round to drop a cent vs backend. Math.ceil prevents undercharge.
        // ──────────────────────────────────────────────────────────────────────
        const toCents = breakdown.roundingApplied ? Math.round : Math.ceil;
        const pricePerItem = toCents(priceEuros * 100);
        const total = pricePerItem * quantity;
        const totalOriginal = baseCents * quantity;
        const totalSaved = totalOriginal - total;
        const perItemSaved = baseCents - pricePerItem;
        const discountPercent = totalOriginal > 0
          ? Math.round((totalSaved / totalOriginal) * 100)
          : 0;

        return {
          // Per-item values (cents)
          perItem: pricePerItem,
          perItemOriginal: baseCents,
          perItemSaved: perItemSaved,

          // Total values (cents)
          total: total,
          totalOriginal: totalOriginal,
          totalSaved: totalSaved,

          // Percentage
          discountPercent: discountPercent,

          // Module-specific
          label: breakdown.label,

          // Booleans for easy x-show
          hasDiscount: totalSaved > 0,
          hasTierDiscount: breakdown.tierDiscount > 0,
          hasModuleDiscount: breakdown.moduleDiscount > 0,

          // Debug info
          breakdown: breakdown
        };
      },

      // ═══════════════════════════════════════════════════════════════════════════
      // TIER MANAGEMENT
      // ═══════════════════════════════════════════════════════════════════════════

      /**
       * Set price testing tier and persist to localStorage
       * @param {string|number|null} tier - Tier value (any string/number, null to clear)
       */
      setTier(tier) {
        // Accept any value, convert to string (null clears the tier)
        const tierValue = tier === null || tier === '' || tier === undefined ? null : String(tier);
        this.tier = tierValue;

        try {
          if (tierValue === null) {
            localStorage.removeItem(PRICING_STORAGE_KEY);
            pricingLog.info('Tier cleared');
          } else {
            const expires = Date.now() + (PRICING_STORAGE_DAYS * 24 * 60 * 60 * 1000);
            localStorage.setItem(PRICING_STORAGE_KEY, JSON.stringify({
              tier: tierValue,
              expires: expires
            }));
            pricingLog.info('Tier set', { tier: tierValue, expires: new Date(expires) });
          }
        } catch (err) {
          pricingLog.warn('Failed to save tier to localStorage', err);
        }
      },

      /**
       * Load tier from localStorage (checks expiry)
       */
      loadTier() {
        try {
          const stored = localStorage.getItem(PRICING_STORAGE_KEY);
          if (!stored) return;

          const { tier, expires } = JSON.parse(stored);
          if (Date.now() > expires) {
            localStorage.removeItem(PRICING_STORAGE_KEY);
            pricingLog.debug('Tier expired, removed');
            return;
          }

          this.tier = tier;
          pricingLog.debug('Tier loaded from storage', { tier });
        } catch (err) {
          pricingLog.warn('Failed to load tier from localStorage, cleaning up', err);
          localStorage.removeItem(PRICING_STORAGE_KEY);
        }
      },

      /**
       * Initialize from URL param (?pt=X) or localStorage
       * Accepts any value for pt param (strings, numbers, etc.)
       */
      initFromURL() {
        const params = new URLSearchParams(window.location.search);
        const pt = params.get('pt');

        if (pt !== null && pt !== '') {
          this.setTier(pt);
          pricingLog.info('Tier set from URL param', { tier: pt });
          return;
        }

        // Fallback to localStorage
        this.loadTier();
      },

      /**
       * Get properties to inject into cart add
       * @param {string|null} discountType - 'quantity_tiers' | null
       * @returns {object} Properties for cart line item
       */
      getCartProperties(discountType = null) {
        const props = {};

        // Only include _pt if tier is set
        if (this.tier !== null) {
          props._pt = String(this.tier);
        }

        if (discountType) {
          props._discountType = discountType;
        }

        return props;
      },

      // ═══════════════════════════════════════════════════════════════════════════
      // INTERNAL HELPERS
      // ═══════════════════════════════════════════════════════════════════════════

      /**
       * Round price UP to nearest charm price target
       * Matches backend: roundToCharmPrice() EXACTLY
       *
       * IMPORTANT: Works in EUROS (floating point) to match backend precision.
       * Backend does floating point math, then charm rounds.
       * If we round to cents first, we lose precision and get wrong results.
       *
       * @param {number} euros - Price in EUROS (floating point, e.g. 17.991)
       * @param {number[]} targets - Charm targets e.g. [0.49, 0.99]
       * @returns {number} Rounded price in EUROS
       *
       * Examples with targets [0.49, 0.99]:
       *   17.23 → 17.49 - smallest target >= .23
       *   17.55 → 17.99 - smallest target >= .55
       *   17.991 → 18.49 - no target >= .991, next dollar + smallest
       */
      _roundToCharm(euros, targets) {
        if (!targets || !targets.length) return euros;

        const sortedTargets = [...targets].sort((a, b) => a - b);
        const floor = Math.floor(euros);
        const decimal = euros - floor;

        // Floating point fix: 17.99 - 17 can give 0.9900000000000002
        // Use epsilon tolerance for comparison
        const epsilon = 0.0001;

        // Find smallest target >= decimal in same dollar
        for (const target of sortedTargets) {
          if (target >= decimal - epsilon) {
            return floor + target;
          }
        }

        // No target >= decimal, go to NEXT dollar + smallest target
        return floor + 1 + sortedTargets[0];
      },

      /**
       * Debug method - logs full pricing state to console
       * Call from console: Alpine.store('pricing').debug()
       */
      debug() {
        const state = {
          tier: this.tier,
          config: this.config,
          localStorage: (() => {
            try {
              return JSON.parse(localStorage.getItem(PRICING_STORAGE_KEY));
            } catch { return null; }
          })(),
          urlParam: new URLSearchParams(window.location.search).get('pt'),
          availableTiers: Object.keys(this.config.priceTesting || {}),
          tierDiscounts: this.config.priceTesting,
          quantityTiers: this.config.quantityTiers,
          rounding: this.config.rounding
        };

        console.group('%c$store.pricing DEBUG', 'color: #9c27b0; font-weight: bold;');
        console.log('%cCurrent Tier:', 'color: #2196f3;', state.tier);
        console.log('%cURL Param (?pt=):', 'color: #2196f3;', state.urlParam);
        console.log('%cLocalStorage:', 'color: #2196f3;', state.localStorage);
        console.log('%cAvailable Tiers:', 'color: #4caf50;', state.availableTiers.length > 0 ? state.availableTiers : 'NONE CONFIGURED');
        console.log('%cTier Discounts:', 'color: #4caf50;', state.tierDiscounts);
        console.log('%cQuantity Tiers:', 'color: #ff9800;', state.quantityTiers);
        console.log('%cRounding:', 'color: #607d8b;', state.rounding);

        // Example calculation
        const example = this.calculate(10000, 1, {});
        console.log('%cExample ($100 base):', 'color: #e91e63;', {
          input: '$100.00',
          output: '$' + (example.perItem / 100).toFixed(2),
          discount: example.discountPercent + '%',
          roundingApplied: example.breakdown.roundingApplied
        });
        console.groupEnd();

        return state;
      },

      /**
       * Initialize store
       */
      init() {
        // Load config from SSR (from shop metafields via theme.liquid)
        // If no config exists, defaults apply (no discounts)
        if (window.Theme?.pricingConfig) {
          this.config = { ...this.config, ...window.Theme.pricingConfig };
        }

        // Initialize tier from URL or localStorage
        this.initFromURL();

        pricingLog.info('$store.pricing initialized', {
          tier: this.tier,
          hasConfig: !!window.Theme?.pricingConfig,
          availableTiers: Object.keys(this.config.priceTesting || {})
        });

        // Auto-debug when ?debug in URL
        if (window.location.search.includes('debug')) {
          setTimeout(() => this.debug(), 100);
        }
      }
    });

    // Initialize pricing store
    Alpine.store('pricing').init();

    // ─────────────────────────────────────────────────────────────────────────────
    // $store.cart - THE ONE API for cart operations
    // ─────────────────────────────────────────────────────────────────────────────

    Alpine.store('cart', {
      // State (reactive, READ-ONLY - synced from Liquid)
      items: ssrCart.items || [],
      item_count: ssrCart.item_count || 0,
      total_price: ssrCart.total_price || 0,
      total_discount: ssrCart.total_discount || 0,
      currency: ssrCart.currency || 'USD',

      // Discount state
      discount_codes: ssrCart.discount_codes || [],
      discountLoading: false,
      discountError: null,

      // UI State
      loading: false,
      itemLoading: {},
      error: null,

      // Undo removal state
      removedItem: null,

      // Internal
      _abortController: null,
      _requestVersion: 0,  // Ensures only latest response morphs DOM

      // ─────────────────────────────────────────────────────────────────────────
      // Getters (derived state)
      // ─────────────────────────────────────────────────────────────────────────

      get isEmpty() {
        return this.item_count === 0;
      },

      get hasItems() {
        return this.item_count > 0;
      },

      get formattedTotal() {
        return formatMoney(this.total_price);
      },

      get formattedDiscount() {
        return this.total_discount > 0 ? formatMoney(this.total_discount) : null;
      },

      get appliedDiscounts() {
        return (this.discount_codes || []).filter(dc => dc.applicable !== false);
      },

      get hasDiscounts() {
        return this.appliedDiscounts.length > 0;
      },

      // Check if any item is loading (for global spinner)
      get isAnyItemLoading() {
        return Object.keys(this.itemLoading).length > 0;
      },

      // Check if specific item is loading (for per-item spinners)
      isItemLoading(key) {
        return this.itemLoading[key] === true;
      },

      // ─────────────────────────────────────────────────────────────────────────
      // Helpers
      // ─────────────────────────────────────────────────────────────────────────

      // Format cents to money string (also available as $money magic)
      formatMoney(cents) {
        return formatMoney(cents);
      },

      // ─────────────────────────────────────────────────────────────────────────
      // Internal: Fetch helpers
      // ─────────────────────────────────────────────────────────────────────────
      //
      // CONCURRENCY MODEL (Last Write Wins):
      //   1. Each request increments _requestVersion
      //   2. Response only morphs DOM if it's still the latest version
      //   3. Loading states persist until final morph (no premature clear)
      //
      //   User clicks: A → B → C (rapid)
      //   Requests:    v1   v2   v3
      //   Responses:   v1 skips, v2 skips, v3 morphs + clears loading
      //
      // ─────────────────────────────────────────────────────────────────────────

      async _fetchCart() {
        try {
          const res = await fetch('/cart.js');
          if (!res.ok) {
            storeLog.error('Failed to get cart', { status: res.status });
            return null;
          }
          return res.json();
        } catch (err) {
          storeLog.error('Failed to get cart', err);
          return null;
        }
      },

      async _postCart(endpoint, body, sourceId = null) {
        // Increment version - only latest response will morph
        const thisVersion = ++this._requestVersion;

        // Abort previous request if still pending
        if (this._abortController) {
          this._abortController.abort();
        }
        this._abortController = new AbortController();

        const sections = collectCartSections();
        if (sections.length > 0) {
          body.sections = sections.join(',');
        }

        try {
          const res = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
            signal: this._abortController.signal
          });

          if (!res.ok) {
            const err = await res.json();
            document.dispatchEvent(new CartErrorEvent(err, sourceId, body));
            throw err;
          }

          const result = await res.json();

          // Only morph if this is still the latest request
          // Prevents stale responses from overwriting newer data
          const isLatest = thisVersion === this._requestVersion;
          if (result.sections && isLatest) {
            morphSections(result.sections);
          } else if (result.sections) {
            storeLog.debug('Skipping stale morph', { thisVersion, current: this._requestVersion });
          }

          // Flag for caller to know if this was the final morph
          result._isLatest = isLatest;
          return result;
        } catch (err) {
          // Handle AbortError silently
          if (err.name === 'AbortError') {
            storeLog.debug('Request aborted', { endpoint });
            return null;
          }
          storeLog.error('Cart operation failed', err);
          throw err;
        }
      },

      _merge(cart) {
        if (!cart) return;
        Object.keys(cart).forEach(key => {
          if (key in this || ['items', 'item_count', 'total_price', 'discount_codes'].includes(key)) {
            this[key] = cart[key];
          }
        });
      },

      // ─────────────────────────────────────────────────────────────────────────
      // Actions
      // ─────────────────────────────────────────────────────────────────────────

      /**
       * Refresh cart from server
       */
      async refresh() {
        this.loading = true;
        this.error = null;

        const cart = await this._fetchCart();
        if (cart) this._merge(cart);

        this.loading = false;
        this.itemLoading = {};
        storeLog.debug('Refreshed', { item_count: this.item_count });
        return cart;
      },

      /**
       * Add item to cart
       * @param {Object} options - { id, quantity?, properties?, selling_plan?, discountType? }
       */
      async add(options) {
        this.loading = true;
        this.error = null;

        // Get pricing properties (tier + optional discountType)
        const pricingStore = Alpine.store('pricing');
        const pricingProps = pricingStore
          ? pricingStore.getCartProperties(options.discountType || null)
          : {};

        const items = options.items || [{
          id: options.id,
          quantity: options.quantity || 1,
          properties: {
            ...pricingProps,
            ...(options.properties || {})
          },
          ...(options.selling_plan && { selling_plan: options.selling_plan })
        }];

        try {
          const result = await this._postCart('/cart/add.js', { items });
          storeLog.info('Added to cart', result);

          // Refresh to get full cart (add returns only added items)
          await this.refresh();

          // Dispatch event for web components
          document.dispatchEvent(new CartUpdateEvent(null, null, { action: 'add', items, result }));

          return result;
        } catch (err) {
          this.error = err.message || 'Add to cart failed';
          this.loading = false;
          throw err;
        }
      },

      /**
       * Change cart item quantity by key (stable, no race conditions)
       * @param {Object} options - { key, quantity }
       */
      async changeItem(options) {
        const key = options.key;

        // Per-item loading (shimmer on specific item, others stay interactive)
        if (key) {
          this.itemLoading = { ...this.itemLoading, [key]: true };
        }
        this.error = null;

        const action = options.quantity === 0 ? 'remove' : 'change';
        storeLog.debug('Changing item', { key, quantity: options.quantity });

        try {
          const cart = await this._postCart('/cart/change.js', {
            id: key,  // Use 'id' (key) instead of 'line' - stable across operations
            quantity: options.quantity
          });

          if (cart) this._merge(cart);

          // Only clear loading after morph (latest response)
          // Stale responses keep items shimmering until final morph
          if (cart?._isLatest) {
            this.itemLoading = {};  // Clear all - morph updated everything
          }

          document.dispatchEvent(new CartUpdateEvent(cart, null, { action }));
          return cart;
        } catch (err) {
          this.error = err.message || 'Update failed';
          // On error, clear this item's loading state
          if (key) {
            const { [key]: _, ...rest } = this.itemLoading;
            this.itemLoading = rest;
          }
          throw err;
        }
      },

      /**
       * Remove item from cart (with exit animation)
       * @param {string} key - Line item key (stable identifier)
       */
      async removeItem(key) {
        // Snapshot item for undo before removal (skip upsell items — they have their own UI)
        // Note: removing a second item while undo is pending silently replaces
        // the first snapshot. Only the most recently removed item is undoable.
        const item = this.items.find(i => i.key === key);
        if (item && !item.properties?._upsell) {
          this.removedItem = {
            id: item.variant_id,
            title: item.product_title,
            image: item.image
              ? (item.image.includes('?')
                  ? item.image.replace(/(\.\w+)\?/, '$1?width=80&')
                  : item.image + '?width=80')
              : null,
            quantity: item.quantity,
            properties: item.properties || {}
          };
        }

        // Animate removal before API call
        const row = document.querySelector(`[data-key="${key}"]`);
        if (row) {
          // Set row height for animation (CSS needs this)
          row.style.setProperty('--row-height', `${row.offsetHeight}px`);
          row.classList.add('removing');

          // Wait for animation to complete (200ms matches CSS)
          await new Promise(resolve => setTimeout(resolve, 200));
        }

        return this.changeItem({ key, quantity: 0 });
      },

      /**
       * Undo last item removal — re-adds the removed item to cart
       */
      async undoRemove() {
        if (!this.removedItem) return;
        const { id, quantity, properties } = this.removedItem;
        this.removedItem = null;
        return this.add({ id, quantity, properties });
      },

      /**
       * Increment item quantity
       * @param {string} key - Line item key
       */
      increment(key) {
        const item = this.items.find(i => i.key === key);
        if (!item) return null;
        return this.changeItem({ key, quantity: item.quantity + 1 });
      },

      /**
       * Decrement item quantity (min 1, does nothing if already at 1)
       * @param {string} key - Line item key
       */
      decrement(key) {
        const item = this.items.find(i => i.key === key);
        if (!item || item.quantity <= 1) return null;
        return this.changeItem({ key, quantity: item.quantity - 1 });
      },

      // ─────────────────────────────────────────────────────────────────────────
      // Discount Actions
      // ─────────────────────────────────────────────────────────────────────────

      _dispatchDiscountError(message) {
        document.dispatchEvent(new CustomEvent('discount-error', {
          bubbles: true,
          detail: { message }
        }));
      },

      _dispatchDiscountSuccess() {
        document.dispatchEvent(new CustomEvent('discount-success', {
          bubbles: true
        }));
      },

      /**
       * Apply a discount code
       * @param {string} code - Discount code to apply
       * @returns {Promise<boolean>} - True if code was applied successfully
       */
      async applyDiscount(code) {
        if (!code || typeof code !== 'string') return false;
        code = code.trim().toUpperCase();
        if (!code) return false;

        storeLog.info('Applying discount code', { code });

        this.discountLoading = true;
        this.discountError = null;

        try {
          // Apply discount via Shopify's /discount endpoint
          await fetch(`/discount/${encodeURIComponent(code)}`, {
            credentials: 'same-origin',
            redirect: 'follow'
          });

          // Re-render sections with updated prices (no-op update triggers morph)
          const cart = await this._postCart('/cart/update.js', {});
          if (cart) this._merge(cart);

          // Check if code was actually applied
          const wasApplied = (this.discount_codes || []).some(
            dc => dc.code.toUpperCase() === code && dc.applicable !== false
          );

          if (!wasApplied) {
            this.discountError = 'This code cannot be applied to your cart';
            this._dispatchDiscountError(this.discountError);
            storeLog.warn('Discount code not applicable', { code });
          } else {
            this._dispatchDiscountSuccess();
            storeLog.info('Discount code applied', { code });
          }

          return wasApplied;
        } catch (err) {
          storeLog.error('Failed to apply discount', { code, error: err });
          this.discountError = 'Could not apply discount code';
          this._dispatchDiscountError(this.discountError);
          return false;
        } finally {
          this.discountLoading = false;
        }
      },

      /**
       * Remove a discount code
       * @param {string} codeToRemove - Discount code to remove
       * @returns {Promise<boolean>} - True if successful
       */
      async removeDiscount(codeToRemove) {
        if (!codeToRemove || typeof codeToRemove !== 'string') return false;
        codeToRemove = codeToRemove.trim().toUpperCase();

        storeLog.info('Removing discount code', { code: codeToRemove });

        this.discountLoading = true;
        this.discountError = null;

        try {
          // Get codes to keep (all except the one being removed)
          const codesToKeep = (this.discount_codes || [])
            .filter(dc => dc.applicable !== false && dc.code.toUpperCase() !== codeToRemove)
            .map(dc => dc.code);

          // Clear all discounts first
          await fetch('/cart/update.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({ discount: '' })
          });

          // Re-apply remaining codes one by one
          for (const code of codesToKeep) {
            await fetch(`/discount/${encodeURIComponent(code)}`, {
              credentials: 'same-origin',
              redirect: 'follow'
            });
          }

          // Re-render sections with updated prices (no-op update triggers morph)
          const cart = await this._postCart('/cart/update.js', {});
          if (cart) this._merge(cart);

          storeLog.info('Discount removed', { removed: codeToRemove, kept: codesToKeep });
          return true;
        } catch (err) {
          storeLog.error('Failed to remove discount', { code: codeToRemove, error: err });
          this.discountError = 'Could not remove discount code';
          return false;
        } finally {
          this.discountLoading = false;
        }
      },

      clearDiscountError() {
        this.discountError = null;
      },

      /**
       * Initialize store
       */
      init() {
        storeLog.info('$store.cart initialized', { item_count: this.item_count });
      }
    });

    log.info('Alpine stores registered');
  };

  // Setup when Alpine is ready
  document.addEventListener('alpine:init', setupAlpineStores);

  log.info('Alpine stores v1.0.0 loaded');
})();
